import React, { useState, useEffect, useMemo } from "react";
import "./OfflinePerformanceReport.css";
import axios from "../../axios";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from "@mui/material";
import { toast } from "react-toastify";
import { Download, Eye, X } from "lucide-react";
import { IP } from "../../internalServer";

// ─── Helper Functions ──────────────────────────────────────────────

const statusConfig = {
  Published: {
    className: "status-badge status-published",
    icon: "✓",
  },
  Generated: {
    className: "status-badge status-generated",
    icon: "📄",
  },
  Draft: {
    className: "status-badge status-draft",
    icon: "✏️",
  },
  approved: {
    className: "status-badge status-not-generated",
    icon: "✕",
  },
};

const mapApiStatus = (apiStatus) => {
  const statusMap = {
    approved: "Approved",
    generated: "Generated",
    published: "Published",
    draft: "Not Generated",
    "not-generated": "Not Generated",
    not_generated: "Not Generated",
    notGenerated: "Not Generated",
  };

  return statusMap[apiStatus?.toLowerCase()] || "Not Generated";
};

const formatDate = (dateString) => {
  if (!dateString || dateString === "—") return "—";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateString;
  }
};

// ─── Excel Export Helper ──────────────────────────────────────────────

const exportToExcel = (students) => {
  const headers = [
    "Admission No.",
    "Student Name",
    "Status",
    "Rank",
    "Overall %",
    "Attendance %",
    "Version",
  ];

  const rows = students.map((student) => [
    student.admissionNo,
    student.name,
    student.status,
    student.rank ?? "—",
    student.percentage !== undefined ? `${student.percentage}%` : "—",
    student.attendancePercentage !== undefined
      ? `${student.attendancePercentage}%`
      : "—",
    `V${student.version}`,
  ]);

  const csv = [headers, ...rows]
    .map((row) =>
      row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
    )
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `Students_Report_Card_Status.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// ─── Status Actions Component ──────────────────────────────────────

const StatusActions = ({
  status,
  student,
  report,
  reportCycles,
  standardData,
  onPreview,
  onDelete,
  onEdit,
  schoolData,
  standardArr,
  sectionArr,
}) => {
  const UserInformation = JSON.parse(localStorage.getItem("UserInformation"));
  const schoolLogo = `${IP}${UserInformation?.school?.profile_pic}`;
  const schoolName = `${UserInformation?.school?.name}`;
  const [showPreview, setShowPreview] = useState(false);

  if (status === "Generated" || status === "Published") {
    return (
      <div className="action-buttons">
        <button
          className="action-btn action-btn-view border border-white"
          onClick={() => setShowPreview(true)}
          title="Preview"
        >
          <Eye size={16} />
        </button>
        {showPreview && (
          <div className="modal-overlay" onClick={() => setShowPreview(false)}>
            <div
              className="modal-content-report"
              onClick={(e) => e.stopPropagation()}
            >
              <ReportCardPreview
                report={report}
                student={student}
                reportCycles={reportCycles}
                standardData={standardData}
                schoolName={schoolName}
                schoolLogo={schoolLogo}
                standardArr={standardArr}
                sectionArr={sectionArr}
                setShowPreview={setShowPreview}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
};

// ─── Report Card Preview Component ──────────────────────────────────

const ReportCardPreview = ({
  report,
  student,
  reportCycles = [],
  standardData = null,
  schoolName = "School Name",
  schoolLogo,
  standardArr,
  sectionArr,
  setShowPreview,
}) => {
  const processedReport = useMemo(() => {
    if (!report) return null;
    return report;
  }, [report]);

  const studentInfo = processedReport?.studentId || {};
  const subjects = processedReport?.reportData?.subjects || [];
  const competencies = processedReport?.reportData?.competencies || [];
  const studentProfilePic = `${IP}${studentInfo.profilePicture}`;

  const academicSubjects = subjects.filter(
    (sub) => sub.subjectCategory === "SCHOLASTIC",
  );

  const coScholasticSubjects = subjects.filter(
    (sub) => sub.subjectCategory === "CO_SCHOLASTIC",
  );

  const cycleName =
    reportCycles.find((c) => c._id === processedReport?.reportCycleId)?.name ||
    "—";

  const selectedClassName =
    standardArr?.find((item) => item?.id === processedReport?.standardId)
      ?.name || "—";

  const selectedSectionName =
    sectionArr?.find((item) => item?.id === processedReport?.sectionId)?.name ||
    "—";

  const eventSet = new Set();
  const eventMaxMarks = {};

  subjects.forEach((sub) => {
    const types = sub.types || [];
    types.forEach((type) => {
      const events = type.events || [];
      events.forEach((evt) => {
        const key = evt.event || "—";
        eventSet.add(key);
        if (evt.maxMarks && evt.maxMarks !== "—") {
          eventMaxMarks[key] = evt.maxMarks;
        }
      });
    });
  });

  const eventColumns = Array.from(eventSet).sort((a, b) => {
    const order = [
      "PT1",
      "PT2",
      "Half Yearly",
      "Final Exam",
      "Mid Term",
      "End Term",
      "PT-1",
      "PT-2",
      "Periodic Test 1",
      "Periodic Test 2",
    ];
    const idxA = order.findIndex((o) =>
      a.toLowerCase().includes(o.toLowerCase()),
    );
    const idxB = order.findIndex((o) =>
      b.toLowerCase().includes(o.toLowerCase()),
    );
    if (idxA !== -1 && idxB !== -1) return idxA - idxB;
    if (idxA !== -1) return -1;
    if (idxB !== -1) return 1;
    return a.localeCompare(b);
  });

  const groupedCompetencies = useMemo(() => {
    const groups = {};
    competencies.forEach((c) => {
      const domain = c.domain || "General Competencies";
      if (!groups[domain]) groups[domain] = [];
      groups[domain].push(c);
    });
    return groups;
  }, [competencies]);

  const getRubricValue = (rubricValue) => {
    const val = (rubricValue || "").toString().trim();
    if (!val) return "—";
    return val;
  };

  const buildTableRows = (subjectList) => {
    const rows = [];

    subjectList.forEach((sub) => {
      const types = sub.types || [];
      const subjectRowCount = types.length > 0 ? types.length : 1;

      if (types.length === 0) {
        rows.push({
          subject: sub.subject || "—",
          assessmentType: sub.assessmentType || "—",
          scores: {},
          totalMarks: {
            obtained: sub.obtainedMarks || "-",
            max: sub.totalMarks || "-",
          },
          percentage: sub.percentage || "-",
          grade: sub.grade || "-",
          rank: sub.rank || "-",
          teacherRemarks: sub.teacherRemarks || "",
          isFirstSubjectRow: true,
          subjectRowCount,
        });
      } else {
        types.forEach((type, index) => {
          const scores = {};

          (type.events || []).forEach((evt) => {
            scores[evt.event] = {
              obtained: evt.obtainedMarks || "-",
              max: evt.maxMarks || "-",
            };
          });

          rows.push({
            subject: sub.subject,
            assessmentType: type.assessmentType,
            scores,
            combineMarks: {
              obtainedMarks: type.obtainedMarks || "-",
              totalMarks: type.totalMarks || "-",
            },
            percentage: type.percentage || "-",
            grade: type.grade || "-",
            rank: type.rank || "-",
            teacherRemarks: type.teacherRemarks || "",
            isFirstSubjectRow: index === 0,
            subjectRowCount,
          });
        });
      }
    });

    return rows;
  };

  const academicTableRows = buildTableRows(academicSubjects);
  const coScholasticTableRows = buildTableRows(coScholasticSubjects);

  const totalEventWidth = 40;
  let eventSectionWidth;
  if (eventColumns.length === 1) eventSectionWidth = 15;
  else if (eventColumns.length === 2) eventSectionWidth = 24;
  else if (eventColumns.length === 3) eventSectionWidth = 30;
  else eventSectionWidth = totalEventWidth;

  const eventColWidth =
    eventColumns.length > 0 ? eventSectionWidth / eventColumns.length : 0;

  const gradeScale = [
    ["91-100", "A1", "51-60", "C1"],
    ["81-90", "A2", "41-50", "C2"],
    ["71-80", "B1", "33-40", "D"],
    ["61-70", "B2", "0-32", "E"],
  ];

  const styles = {
    container: {
      maxHeight: "90vh",
      overflowY: "auto",
      paddingBottom: "10px",
      margin: "0 auto",
      padding: "0",
      backgroundColor: "#ffffff",
      borderRadius: "12px",
      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
    },
    header: {
      position: "relative",
      backgroundColor: "#0f172a",
      padding: "24px 32px",
      textAlign: "center",
      color: "#ffffff",
    },
    schoolName: {
      fontSize: "28px",
      fontWeight: "700",
      letterSpacing: "0.025em",
      color: "#34d399",
      margin: "0 0 4px 0",
    },
    schoolAddress: {
      fontSize: "14px",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      color: "#cbd5e1",
      margin: "8px 0 0 0",
    },
    reportBadge: {
      display: "inline-block",
      backgroundColor: "#fbbf24",
      padding: "8px 32px",
      marginTop: "16px",
      borderRadius: "4px",
    },
    reportBadgeText: {
      fontSize: "14px",
      fontWeight: "700",
      color: "#0f172a",
    },
    infoBox: {
      border: "2px solid #000000",
      borderTop: "none",
      padding: "24px 32px",
      backgroundColor: "#ffffff",
    },
    infoGrid: {
      display: "grid",
      gridTemplateColumns: "1fr auto",
      gap: "16px",
    },
    infoLeft: {
      display: "flex",
      flexDirection: "column",
      gap: "12px",
    },
    infoRow: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      fontSize: "14px",
    },
    infoLabel: {
      minWidth: "120px",
      fontWeight: "700",
      color: "#334155",
    },
    infoValueFlex: {
      flex: 1,
      borderBottom: "2px dotted #94a3b8",
      padding: "0 8px 4px 8px",
      textAlign: "center",
      fontWeight: "600",
      color: "#0f172a",
    },
    infoValue: {
      borderBottom: "2px dotted #94a3b8",
      padding: "0 8px 4px 8px",
      textAlign: "center",
      fontWeight: "600",
      color: "#0f172a",
    },
    photoBox: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "100px",
      height: "120px",
      border: "2px solid #000000",
      backgroundColor: "#f8fafc",
    },
    photoText: {
      fontSize: "12px",
      color: "#94a3b8",
    },
    sectionTitle: {
      display: "flex",
      alignItems: "center",
    },
    sectionNumber: {
      backgroundColor: "#1e3a5f",
      color: "#ffffff",
      padding: "4px 12px",
      fontSize: "14px",
      fontWeight: "700",
    },
    sectionLabel: {
      backgroundColor: "#1e3a5f",
      color: "#ffffff",
      padding: "4px 16px",
      fontSize: "14px",
      fontWeight: "700",
    },
    sectionNumberCo: {
      backgroundColor: "#683393",
      color: "#ffffff",
      padding: "4px 12px",
      fontSize: "14px",
      fontWeight: "700",
    },
    sectionLabelCo: {
      backgroundColor: "#683393",
      color: "#ffffff",
      padding: "4px 16px",
      fontSize: "14px",
      fontWeight: "700",
    },
    tableWrapper: {
      padding: "0 32px 16px 32px",
      overflowX: "auto",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      border: "2px solid #000000",
      fontSize: "14px",
    },
    tableHead: {
      backgroundColor: "#1e3a5f",
      color: "#ffffff",
    },
    tableHeader: {
      border: "2px solid #000000",
      padding: "12px 8px",
      textAlign: "center",
      fontWeight: "700",
      fontSize: "14px",
    },
    tableHeaderSub: {
      backgroundColor: "#e8f0fe",
    },
    tableHeaderSubCell: {
      border: "2px solid #000000",
      padding: "8px 8px",
      textAlign: "center",
      fontSize: "12px",
      fontWeight: "700",
      color: "#000000",
    },
    tableCell: {
      border: "2px solid #000000",
      padding: "10px 8px",
      textAlign: "center",
      fontSize: "14px",
    },
    tableCellLeft: {
      border: "2px solid #000000",
      padding: "10px 12px",
      textAlign: "left",
      fontSize: "14px",
    },
    tableCellBold: {
      fontWeight: "700",
    },
    rowEven: {
      backgroundColor: "#ffffff",
    },
    rowOdd: {
      backgroundColor: "#f8fafc",
    },
    overallRow: {
      backgroundColor: "#e8f0fe",
    },
    overallRowCo: {
      backgroundColor: "#f0e8f5",
    },
    summaryWrapper: {
      padding: "0 32px 16px 32px",
    },
    summaryGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(4, 1fr)",
      gap: "16px",
    },
    summaryCard: {
      border: "2px solid #cbd5e1",
      borderRadius: "8px",
      padding: "8px",
      textAlign: "center",
    },
    summaryLabel: {
      fontSize: "12px",
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: "0.025em",
      color: "#64748b",
    },
    summaryValue: {
      fontSize: "20px",
      fontWeight: "700",
      color: "#1e3a8a",
      marginTop: "4px",
    },
    gradingWrapper: {
      padding: "0 32px 16px 32px",
      display: "flex",
      justifyContent: "flex-end",
    },
    gradingBox: {
      width: "256px",
      border: "2px solid #000000",
    },
    gradingTitle: {
      borderBottom: "2px solid #000000",
      padding: "8px",
      textAlign: "center",
      fontSize: "14px",
      fontWeight: "700",
    },
    gradingGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(4, 1fr)",
      fontSize: "12px",
    },
    gradingCell: {
      padding: "6px 8px",
      textAlign: "center",
      borderRight: "2px solid #000000",
    },
    gradingCellLast: {
      padding: "6px 8px",
      textAlign: "center",
    },
    gradingCellBold: {
      fontWeight: "700",
    },
    footer: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: "24px",
      padding: "24px 32px",
      borderTop: "2px solid #000000",
    },
    signatureBox: {
      textAlign: "center",
    },
    signatureLine: {
      borderBottom: "2px solid #000000",
      margin: "0 auto 8px auto",
      width: "160px",
      height: "32px",
    },
    signatureLabel: {
      fontSize: "14px",
      fontWeight: "700",
      color: "#475569",
    },
    noData: {
      padding: "24px",
      textAlign: "center",
      color: "#94a3b8",
      fontSize: "16px",
    },
    schoolLogoStyle: {
      position: "absolute",
      top: 25,
      left: 20,
      width: "100px",
      height: "100px",
      borderRadius: "50%",
    },
  };

  const renderTable = (tableRows, isCoScholastic = false) => {
    const headerBg = isCoScholastic ? styles.tableHead : styles.tableHead;
    const headerBgColor = isCoScholastic ? "#683393" : "#1e3a5f";
    const overallBg = isCoScholastic ? styles.overallRowCo : styles.overallRow;

    return (
      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr style={{ ...headerBg, backgroundColor: headerBgColor }}>
              <th style={{ ...styles.tableHeader, width: "12%" }}>Subject</th>
              <th style={{ ...styles.tableHeader, width: "15%" }}>
                Assessment Type
              </th>
              <th
                colSpan={Math.max(eventColumns.length, 1)}
                style={{
                  ...styles.tableHeader,
                }}
              >
                Term Assessments (Marks)
              </th>
              <th style={{ ...styles.tableHeader, width: "10%" }}>
                Total Marks
              </th>
              <th style={{ ...styles.tableHeader, width: "15%" }}>
                Percentage (%)
              </th>
              <th style={{ ...styles.tableHeader, width: "10%" }}>Grade</th>
              <th style={{ ...styles.tableHeader, width: "10%" }}>Rank</th>
              <th style={{ ...styles.tableHeader, width: "20%" }}>
                Teacher Remarks
              </th>
            </tr>
            <tr style={styles.tableHeaderSub}>
              <th style={styles.tableHeaderSubCell}></th>
              <th style={styles.tableHeaderSubCell}></th>
              {eventColumns.map((eventName, idx) => (
                <th
                  key={`evt-${idx}`}
                  style={{
                    ...styles.tableHeaderSubCell,
                    width: `${eventColWidth}%`,
                  }}
                >
                  {eventName}
                </th>
              ))}
              {eventColumns.length === 0 && (
                <th style={styles.tableHeaderSubCell}></th>
              )}
              <th style={styles.tableHeaderSubCell}></th>
              <th style={styles.tableHeaderSubCell}></th>
              <th style={styles.tableHeaderSubCell}></th>
              <th style={styles.tableHeaderSubCell}></th>
              <th style={styles.tableHeaderSubCell}></th>
            </tr>
          </thead>

          <tbody>
            {tableRows.length === 0 ? (
              <tr>
                <td colSpan={8} style={styles.noData}>
                  No {isCoScholastic ? "co-scholastic" : "academic"} data
                  available.
                </td>
              </tr>
            ) : (
              tableRows.map((row, index) => (
                <tr
                  key={`${row.subject}-${row.assessmentType}-${index}`}
                  style={index % 2 === 0 ? styles.rowEven : styles.rowOdd}
                >
                  <td
                    style={{
                      ...styles.tableCellLeft,
                      fontWeight: "700",
                      width: "12%",
                    }}
                  >
                    {row.subject || ""}
                  </td>
                  <td style={{ ...styles.tableCell, width: "15%" }}>
                    {row.assessmentType}
                  </td>
                  {eventColumns.map((eventName, idx) => {
                    const score = row.scores[eventName];
                    return (
                      <td
                        key={`sc-${idx}`}
                        style={{
                          ...styles.tableCell,
                          width: `${eventColWidth}%`,
                        }}
                      >
                        {score && typeof score === "object"
                          ? `${score.obtained} - [${score.max}]`
                          : "—"}
                      </td>
                    );
                  })}
                  {eventColumns.length === 0 && (
                    <td style={styles.tableCell}>—</td>
                  )}
                  <td
                    style={{
                      ...styles.tableCell,
                      ...styles.tableCellBold,
                      width: "10%",
                    }}
                  >
                    {row.combineMarks?.obtainedMarks || "-"} - [
                    {row.combineMarks?.totalMarks || "-"}]
                  </td>
                  <td style={{ ...styles.tableCell, width: "10%" }}>
                    {row.percentage ? `${row.percentage}%` : "—"}
                  </td>
                  <td
                    style={{
                      ...styles.tableCell,
                      ...styles.tableCellBold,
                      width: "10%",
                    }}
                  >
                    {row.grade ?? "—"}
                  </td>
                  <td style={{ ...styles.tableCell, width: "10%" }}>
                    {row.rank ?? "—"}
                  </td>
                  <td
                    style={{
                      ...styles.tableCellLeft,
                      width: "20%",
                    }}
                  >
                    {row.teacherRemarks || "—"}
                  </td>
                </tr>
              ))
            )}

            {processedReport && (
              <tr style={overallBg}>
                <td
                  colSpan={isCoScholastic ? 5 : 2}
                  style={{
                    ...styles.tableCellLeft,
                    fontWeight: "700",
                    border: "2px solid #000000",
                  }}
                >
                  {isCoScholastic
                    ? "OVERALL GRADE (Co-Scholastic)"
                    : "OVERALL (All Subjects)"}
                </td>
                {!isCoScholastic &&
                  eventColumns.map((_, idx) => (
                    <td
                      key={`ov-${idx}`}
                      style={{
                        ...styles.tableCell,
                        border: "2px solid #000000",
                      }}
                    >
                      —
                    </td>
                  ))}
                {!isCoScholastic && eventColumns.length === 0 && (
                  <td
                    style={{ ...styles.tableCell, border: "2px solid #000000" }}
                  >
                    —
                  </td>
                )}
                {isCoScholastic ? (
                  <td
                    colSpan={4}
                    style={{
                      ...styles.tableCell,
                      fontWeight: "700",
                      border: "2px solid #000000",
                      width: "10%",
                    }}
                  >
                    {processedReport?.overallGrade ?? "—"}
                  </td>
                ) : (
                  <>
                    <td
                      style={{
                        ...styles.tableCell,
                        fontWeight: "700",
                        border: "2px solid #000000",
                        width: "10%",
                      }}
                    >
                      {processedReport?.obtainedMarks ?? "—"} /{" "}
                      {processedReport?.totalMarks ?? "—"}
                    </td>
                    <td
                      style={{
                        ...styles.tableCell,
                        fontWeight: "700",
                        border: "2px solid #000000",
                        width: "10%",
                      }}
                    >
                      {processedReport?.percentage
                        ? `${processedReport.percentage}%`
                        : "—"}
                    </td>
                    <td
                      style={{
                        ...styles.tableCell,
                        fontWeight: "700",
                        border: "2px solid #000000",
                        width: "10%",
                      }}
                    >
                      {processedReport?.overallGrade ?? "—"}
                    </td>
                    <td
                      style={{
                        ...styles.tableCell,
                        fontWeight: "700",
                        border: "2px solid #000000",
                        width: "10%",
                      }}
                    >
                      {processedReport?.rank ?? "—"}
                    </td>
                    <td
                      style={{
                        ...styles.tableCellLeft,
                        fontWeight: "700",
                        border: "2px solid #000000",
                        width: "20%",
                      }}
                    >
                      {processedReport?.teacherRemarks || "Good Performance"}
                    </td>
                  </>
                )}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const renderCompetencyTable = () => {
    if (competencies.length === 0) {
      return null;
    }

    const competencyStyles = {
      wrapper: {
        padding: "0 32px 16px 32px",
      },
      titleContainer: {
        display: "flex",
        alignItems: "center",
        marginBottom: "0px",
      },
      titleNumber: {
        backgroundColor: "#2e7d32",
        color: "#ffffff",
        padding: "4px 12px",
        fontSize: "14px",
        fontWeight: "700",
      },
      titleText: {
        backgroundColor: "#2e7d32",
        color: "#ffffff",
        padding: "4px 16px",
        fontSize: "14px",
        fontWeight: "700",
      },
      table: {
        width: "100%",
        borderCollapse: "collapse",
        border: "2px solid #000000",
        fontSize: "14px",
      },
      headerRow: {
        backgroundColor: "#e8f5e9",
      },
      headerCell: {
        border: "2px solid #000000",
        padding: "12px 8px",
        textAlign: "center",
        fontWeight: "700",
        fontSize: "14px",
        color: "#1b5e20",
      },
      domainRow: {
        backgroundColor: "#c8e6c9",
      },
      domainCell: {
        border: "2px solid #000000",
        padding: "10px 12px",
        textAlign: "left",
        fontWeight: "700",
        fontSize: "14px",
        color: "#1b5e20",
      },
      rowEven: {
        backgroundColor: "#ffffff",
      },
      rowOdd: {
        backgroundColor: "#f1f8e9",
      },
      cell: {
        border: "2px solid #000000",
        padding: "10px 8px",
        textAlign: "left",
        fontSize: "14px",
      },
      cellCenter: {
        border: "2px solid #000000",
        padding: "10px 8px",
        textAlign: "center",
        fontSize: "14px",
      },
      gradeText: {
        fontWeight: "700",
        color: "#2e7d32",
        fontSize: "14px",
      },
    };

    return (
      <div style={competencyStyles.wrapper}>
        <div style={competencyStyles.titleContainer}>
          <div style={competencyStyles.titleNumber}>3</div>
          <div style={competencyStyles.titleText}>COMPETENCY ASSESSMENT</div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={competencyStyles.table}>
            <thead>
              <tr style={competencyStyles.headerRow}>
                <th style={{ ...competencyStyles.headerCell, width: "30%" }}>
                  Domain Name
                </th>
                <th style={{ ...competencyStyles.headerCell, width: "30%" }}>
                  Grade
                </th>
                <th style={{ ...competencyStyles.headerCell, width: "40%" }}>
                  Remarks
                </th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(groupedCompetencies).map(
                ([domain, domainCompetencies]) => (
                  <React.Fragment key={domain}>
                    <tr style={competencyStyles.domainRow}>
                      <td colSpan={3} style={competencyStyles.domainCell}>
                        {domain}
                      </td>
                    </tr>
                    {domainCompetencies.map((comp, idx) => {
                      const rubricValue = getRubricValue(comp.rubricValue);
                      return (
                        <tr
                          key={`${domain}-${idx}`}
                          style={
                            idx % 2 === 0
                              ? competencyStyles.rowEven
                              : competencyStyles.rowOdd
                          }
                        >
                          <td style={competencyStyles.cell}>
                            {comp.competency || "—"}
                          </td>
                          <td style={competencyStyles.cellCenter}>
                            <span style={competencyStyles.gradeText}>
                              {rubricValue}
                            </span>
                          </td>
                          <td style={competencyStyles.cell}>
                            {comp.remark || "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ),
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        {schoolLogo ? (
          <img src={schoolLogo} style={styles.schoolLogoStyle} />
        ) : (
          ""
        )}
        <X
          color="#fff"
          className="mt-3"
          style={{
            position: "absolute",
            top: "10px",
            right: "17",
            cursor: "pointer",
          }}
          onClick={() => setShowPreview(false)}
        />
        <h2 style={styles.schoolName}>{schoolName}</h2>
        <p style={styles.schoolAddress}></p>
        <div style={styles.reportBadge}>
          <span style={styles.reportBadgeText}>REPORT CARD</span>
        </div>
      </div>

      <div style={styles.infoBox}>
        <div style={styles.infoGrid}>
          <div style={styles.infoLeft}>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Student Name :</span>
              <span style={styles.infoValueFlex}>
                {studentInfo.name || student?.name || "—"}
              </span>
              <span style={{ ...styles.infoLabel, minWidth: "110px" }}>
                Admission No :
              </span>
              <span style={{ ...styles.infoValue, width: "112px" }}>
                {studentInfo.addmissionNo ||
                  studentInfo.admissionNo ||
                  student?.admissionNo ||
                  "—"}
              </span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Class & Section :</span>
              <span style={{ ...styles.infoValue, width: "128px" }}>
                {selectedClassName} - {selectedSectionName}
              </span>
              <span style={{ ...styles.infoLabel, minWidth: "110px" }}>
                Report Cycle :
              </span>
              <span style={styles.infoValueFlex}>{cycleName}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Mother's Name :</span>
              <span style={styles.infoValueFlex}>
                {studentInfo.mother?.name || "—"}
              </span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Father's Name :</span>
              <span style={styles.infoValueFlex}>
                {studentInfo.father?.name || "—"}
              </span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Date of Birth :</span>
              <span style={styles.infoValueFlex}>
                {studentInfo.dateOfBirth
                  ? formatDate(studentInfo.dateOfBirth)
                  : "—"}
              </span>
              <span style={{ ...styles.infoLabel, minWidth: "110px" }}>
                Contact :
              </span>
              <span style={{ ...styles.infoValue, width: "112px" }}>
                {studentInfo.father?.mobileNumber || "—"}
              </span>
            </div>
          </div>
          <div style={styles.photoBox}>
            {studentProfilePic ? (
              <img src={studentProfilePic} style={styles.photoBox} />
            ) : (
              <span style={styles.photoText}>Student Photo</span>
            )}
          </div>
        </div>
      </div>

      <div style={{ padding: "16px 0 0 0" }}>
        <div style={{ padding: "0 32px" }}>
          <div style={styles.sectionTitle}>
            <div style={styles.sectionNumber}>1</div>
            <div style={styles.sectionLabel}>ACADEMIC PERFORMANCE</div>
          </div>
        </div>
        {renderTable(academicTableRows, false)}
      </div>

      {processedReport && (
        <div style={styles.summaryWrapper}>
          <div style={styles.summaryGrid}>
            <div style={styles.summaryCard}>
              <p style={styles.summaryLabel}>Overall Marks</p>
              <p style={styles.summaryValue}>
                {processedReport.obtainedMarks}/{processedReport.totalMarks}
              </p>
            </div>
            <div style={styles.summaryCard}>
              <p style={styles.summaryLabel}>Overall Percentage</p>
              <p style={styles.summaryValue}>{processedReport.percentage}%</p>
            </div>
            <div style={styles.summaryCard}>
              <p style={styles.summaryLabel}>Overall Grade</p>
              <p style={styles.summaryValue}>{processedReport.overallGrade}</p>
            </div>
            <div style={styles.summaryCard}>
              <p style={styles.summaryLabel}>Overall Rank</p>
              <p style={styles.summaryValue}>{processedReport.rank}</p>
            </div>
          </div>
        </div>
      )}

      {coScholasticSubjects.length > 0 && (
        <div style={{ padding: "16px 0 0 0" }}>
          <div style={{ padding: "0 32px" }}>
            <div style={styles.sectionTitle}>
              <div style={styles.sectionNumberCo}>2</div>
              <div style={styles.sectionLabelCo}>CO-SCHOLASTIC PERFORMANCE</div>
            </div>
          </div>
          {renderTable(coScholasticTableRows, true)}
        </div>
      )}

      {renderCompetencyTable()}

      <div style={styles.gradingWrapper}>
        <div style={styles.gradingBox}>
          <p style={styles.gradingTitle}>Grading Scale</p>
          <div style={styles.gradingGrid}>
            {gradeScale.map((row, idx) => (
              <React.Fragment key={idx}>
                <div style={styles.gradingCell}>{row[0]}</div>
                <div style={{ ...styles.gradingCell, fontWeight: "700" }}>
                  {row[1]}
                </div>
                <div style={styles.gradingCell}>{row[2]}</div>
                <div style={{ ...styles.gradingCellLast, fontWeight: "700" }}>
                  {row[3]}
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      <div style={styles.footer}>
        <div style={styles.signatureBox}>
          <div style={styles.signatureLine}></div>
          <p style={styles.signatureLabel}>Class Teacher Sign</p>
        </div>
        <div style={styles.signatureBox}>
          <div style={styles.signatureLine}></div>
          <p style={styles.signatureLabel}>Parent's Sign</p>
        </div>
        <div style={styles.signatureBox}>
          <div style={styles.signatureLine}></div>
          <p style={styles.signatureLabel}>Principal Sign</p>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────

const OfflinePerformanceReport = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [classId, setClassId] = useState(null);
  const [sectionId, setSectionId] = useState(null);
  const [showTable, setShowTable] = useState(false);

  const [academicyearId, setAcademicYearId] = useState("");
  const [academicSessionSelect, setAcademicSessionSelect] = useState("");
  const [selectedTimeTableSession, setSelectedTimeTableSession] = useState("");

  const [selectedStandardId, setSelectedStandardId] = useState("");
  const [selectedSectionId, setSelectedSectionId] = useState("");
  const [selectedReportCycleId, setSelectedReportCycleId] = useState("");
  const [selectedStudents, setSelectedStudents] = useState([]);

  const [bulkLoading, setBulkLoading] = useState({
    generate: false,
    publish: false,
    regenerate: false,
  });

  const CTRole_Information = JSON.parse(
    localStorage.getItem("role_as_class_teacher"),
  );
  const CoRole_Information = JSON.parse(
    localStorage.getItem("role_as_co_ordinator"),
  );
  const schoolId = JSON.parse(localStorage.getItem("schoolId"));

  const [schoolData, setSchoolData] = useState(null);
  const [standardData, setStandardData] = useState(null);
  const [reportCycles, setReportCycles] = useState([]);
  const [classReports, setClassReports] = useState([]);

  const handleClassChange = (value) => {
    setClassId(value);
    setShowTable(false);
    setSectionId("");
  };

  const handleSectionChange = (value) => {
    setSectionId(value);
    setShowTable(false);
  };

  let sectionArr = [];
  let standardArr = [];

  const dropDownHelper = () => {
    function addUniqueObjectToArraystand(obj) {
      if (
        !standardArr.some(
          (item) => JSON.stringify(item) === JSON.stringify(obj),
        )
      ) {
        standardArr.push(obj);
      }
    }
    CTRole_Information?.map((obj) =>
      addUniqueObjectToArraystand(obj.standard_id),
    );
    CoRole_Information?.map((obj) =>
      addUniqueObjectToArraystand(obj.standard_id),
    );
  };

  dropDownHelper();

  const handleDropDownSec = () => {
    function addUniqueObjectToArraysect(obj) {
      if (
        !sectionArr.some((item) => JSON.stringify(item) === JSON.stringify(obj))
      ) {
        sectionArr.push(obj);
      }
    }
    CTRole_Information?.map((obj) => {
      if (obj?.standard_id?.id === classId) {
        addUniqueObjectToArraysect(obj.section_id);
      }
    });
    CoRole_Information?.map((obj) => {
      if (obj?.standard_id?.id === classId) {
        addUniqueObjectToArraysect(obj.section_id);
      }
    });
  };
  handleDropDownSec();

  const academicSessionId = JSON.parse(
    localStorage.getItem("academicYearSessionId"),
  );
  const timeTableSessionId = JSON.parse(
    localStorage.getItem("timeTableSessionId"),
  );

  useEffect(() => {
    setSelectedStandardId("");
    setSelectedSectionId("");
    setSelectedReportCycleId("");
  }, [academicyearId]);

  useEffect(() => {
    if (!selectedSectionId) {
      setSelectedReportCycleId("");
    }
  }, [selectedSectionId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchQuery,
    selectedStandardId,
    selectedSectionId,
    selectedReportCycleId,
  ]);

  const fetchReportedCycle = async () => {
    if (!schoolId) return;
    try {
      const response = await axios.get(
        `api/v1/performance/report-cycles?schoolId=${schoolId}`,
      );
      setReportCycles(response?.data?.data);
    } catch (err) {
      console.log("Error", err);
    }
  };

  useEffect(() => {
    fetchReportedCycle();
  }, []);

  const fetchStudentData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        `api/v1/performance/engine/class-reports?schoolId=${schoolId}&standardId=${classId}&sectionId=${sectionId}&reportCycleId=${selectedReportCycleId}&academicYearSessionId=${academicSessionId}&timetableSessionId=${timeTableSessionId}`,
      );

      const reports = response?.data?.data || [];

      setClassReports(reports);

      const formattedStudents = reports.map((item) => ({
        _id: item?.studentId?._id,
        report: item,

        admissionNo: item.studentId?.addmissionNo || "-",
        name: item.studentId?.name || "-",

        status: mapApiStatus(item.status),

        rank: item.rank,
        percentage: item.percentage,

        attendancePercentage:
          item.attendanceTotalDays > 0
            ? Math.round(
                (item.attendancePresentDays / item.attendanceTotalDays) * 100,
              )
            : 0,

        version: item.version,

        teacherRemarks: item.teacherRemarks || "",
        heightCm: item.heightCm || "",
        weightKg: item.weightKg || "",
        promotedToClass: item.promotedToClass || "",
      }));

      setStudents(formattedStudents);
    } catch (err) {
      console.log(err);
      setError(err?.response?.data?.message || "Failed to fetch students");
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    try {
      setBulkLoading((prev) => ({
        ...prev,
        generate: true,
      }));

      const payload = {
        schoolId,
        standardId: classId,
        sectionId,
        reportCycleId: selectedReportCycleId,
        academicYearSessionId: academicSessionId,
        timetableSessionId: timeTableSessionId,
        studentIds: [],
        studentsCustomData: {},
      };

      const res = await axios.post(
        "/api/v1/performance/engine/bulk-generate",
        payload,
      );

      toast.success(res.data?.message || "Reports generated successfully");

      fetchStudentData();
      setSelectedStudents([]);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to generate reports");
    } finally {
      setBulkLoading((prev) => ({
        ...prev,
        generate: false,
      }));
    }
  };

  useEffect(() => {
    if (!classId || !sectionId || !selectedReportCycleId) return;
    fetchStudentData();
  }, [classId, sectionId, selectedReportCycleId]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedStudents(paginatedStudents.map((student) => student._id));
    } else {
      setSelectedStudents([]);
    }
  };

  const handleSelectStudent = (studentId) => {
    setSelectedStudents((prev) => {
      if (prev.includes(studentId)) {
        return prev.filter((id) => id !== studentId);
      }
      return [...prev, studentId];
    });
  };

  const hasReportsGenerated = students.length > 0;

  const filteredStudents = useMemo(() => {
    let filtered = students;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          s.admissionNo.toLowerCase().includes(q),
      );
    }
    return filtered;
  }, [students, searchQuery]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredStudents.length / itemsPerPage),
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedStudents = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredStudents.slice(start, start + itemsPerPage);
  }, [filteredStudents, currentPage, itemsPerPage]);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };
  const goToPrevious = () => goToPage(currentPage - 1);
  const goToNext = () => goToPage(currentPage + 1);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage <= 3) {
      for (let i = 1; i <= 4; i++) pages.push(i);
      pages.push("...");
      pages.push(totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(1);
      pages.push("...");
      for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push("...");
      pages.push(currentPage - 1);
      pages.push(currentPage);
      pages.push(currentPage + 1);
      pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  const handleApproved = async () => {
    try {
      setBulkLoading((prev) => ({
        ...prev,
        publish: true,
      }));

      const studentIds =
        selectedStudents.length > 0
          ? selectedStudents
          : students.map((student) => student._id);

      if (studentIds.length === 0) {
        toast.error("No students available for approve!!");
        return;
      }

      const payload = {
        schoolId,
        reportCycleId: selectedReportCycleId,
        standardId: classId,
        sectionId,
        academicYearSessionId: academicSessionId,
        timetableSessionId: timeTableSessionId,
        updates: students.map((student) => ({
          studentId: student._id,
          teacherRemarks: student.teacherRemarks || "",
          heightCm: student.heightCm || null,
          weightKg: student.weightKg || null,
          promotedToClass: student.promotedToClass || "",
        })),
      };

      let res = await axios.post("/api/v1/performance/engine/approve", payload);

      res && toast.success("Report(s) approved successfully");

      fetchStudentData();
      setSelectedStudents([]);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to approve reports.");
    } finally {
      setBulkLoading((prev) => ({
        ...prev,
        publish: false,
      }));
    }
  };

  const handleDownloadClassExcel = () => {
    if (filteredStudents.length === 0) {
      toast.warning("No students to download");
      return;
    }
  };

  const isTopSelectionComplete =
    selectedStandardId && selectedSectionId && selectedReportCycleId;
  const hasGlobalSessions = !!academicyearId && !!selectedTimeTableSession;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, classId, sectionId, selectedReportCycleId, itemsPerPage]);

  // ─── Status Badge Component ───
  const StatusBadge = ({ status }) => {
    const getStatusConfig = (status) => {
      const configs = {
        Published: { bg: "#d1fae5", color: "#065f46", label: "Published" },
        Generated: { bg: "#dbeafe", color: "#1e40af", label: "Generated" },
        Approved: { bg: "#d1fae5", color: "#065f46", label: "Approved" },
        "Not Generated": {
          bg: "#fee2e2",
          color: "#991b1b",
          label: "Not Generated",
        },
        Draft: { bg: "#fef3c7", color: "#92400e", label: "Draft" },
      };
      return configs[status] || configs["Not Generated"];
    };

    const config = getStatusConfig(status);
    return (
      <span
        className="badge rounded-pill px-3 py-2"
        style={{
          backgroundColor: config.bg,
          color: config.color,
          fontSize: "11px",
          fontWeight: "600",
          minWidth: "90px",
          display: "inline-block",
        }}
      >
        {config.label}
      </span>
    );
  };

  return (
    <div className="report-page">
      <div className="report-container">
        {/* Filters Bar */}
        <div className="card shadow-sm mb-3">
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-md-3">
                <FormControl size="small" fullWidth>
                  <InputLabel>Class*</InputLabel>
                  <Select
                    label="Class*"
                    value={classId || ""}
                    onChange={(e) => handleClassChange(e.target.value)}
                  >
                    <MenuItem value="null">Select Class</MenuItem>
                    {standardArr?.length > 0 &&
                      [...standardArr]
                        .sort((a, b) => {
                          const aNum = Number(a.name);
                          const bNum = Number(b.name);
                          const bothNumeric = !isNaN(aNum) && !isNaN(bNum);
                          if (bothNumeric) return aNum - bNum;
                          return a.name.localeCompare(b.name);
                        })
                        .map((standard, index) => (
                          <MenuItem key={index} value={standard?.id}>
                            {standard?.name}
                          </MenuItem>
                        ))}
                  </Select>
                </FormControl>
              </div>

              <div className="col-md-3">
                <FormControl size="small" fullWidth>
                  <InputLabel>Section*</InputLabel>
                  <Select
                    label="Section*"
                    value={sectionId || ""}
                    onChange={(e) => handleSectionChange(e.target.value)}
                  >
                    <MenuItem value="null">Select Section</MenuItem>
                    {sectionArr
                      ?.slice()
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </div>

              <div className="col-md-3">
                <FormControl size="small" fullWidth>
                  <InputLabel>Report Cycle *</InputLabel>
                  <Select
                    label="Report Cycle *"
                    value={selectedReportCycleId}
                    onChange={(e) => {
                      setSelectedReportCycleId(e.target.value);
                      setShowTable(false);
                    }}
                  >
                    <MenuItem value="">Select Report Cycle</MenuItem>
                    {reportCycles?.map((cycle) => (
                      <MenuItem key={cycle._id} value={cycle._id}>
                        {cycle.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              <div className="col-md-3">
                <button
                  className="btn btn-outline-secondary w-100"
                  onClick={() => {
                    setClassId("");
                    setSectionId("");
                    setSelectedReportCycleId("");
                    setSearchQuery("");
                    setCurrentPage(1);
                  }}
                >
                  ⟳ Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Search & Actions */}
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <div
            className="d-flex align-items-center gap-2"
            style={{ flex: "1", maxWidth: "450px" }}
          >
            <div className="position-relative w-100">
              <span
                className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"
                style={{ fontSize: "14px" }}
              >
                🔍
              </span>
              <input
                className="form-control"
                style={{
                  paddingLeft: "40px",
                  height: "40px",
                  borderRadius: "8px",
                }}
                placeholder="Search by student name or admission no."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <button
              className="btn btn-primary"
              onClick={handleGenerate}
              disabled={bulkLoading.generate}
              style={{ borderRadius: "8px" }}
            >
              {bulkLoading.generate ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Generating...
                </>
              ) : (
                "📄 Generate Reports"
              )}
            </button>
            <button
              className="btn btn-success"
              onClick={handleApproved}
              disabled={bulkLoading.publish}
              style={{ borderRadius: "8px" }}
            >
              {bulkLoading.publish ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Publishing...
                </>
              ) : (
                "📤 Approve Reports"
              )}
            </button>
          </div>
        </div>

        {/* Table Card */}
        <div className="card shadow-sm">
          {/* Table Header */}
          <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
            <div className="d-flex align-items-center gap-2">
              <h6 className="mb-0 fw-bold">Total</h6>
              <span className="badge bg-primary rounded-pill">
                {filteredStudents.length} Students
              </span>
            </div>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => fetchStudentData()}
              disabled={loading}
              style={{ borderRadius: "6px" }}
            >
              {loading ? "⟳ Loading..." : "⟳ Refresh"}
            </button>
          </div>

          {/* Table Body with fixed height */}
          <div
            style={{
              height: "340px",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <div
              style={{ height: "100%", overflowY: "auto", overflowX: "auto" }}
            >
              <table
                className="table table-hover table-striped mb-0"
                style={{
                  fontSize: "13px",
                  minWidth: "950px",
                  width: "100%",
                  tableLayout: "fixed",
                }}
              >
                <thead
                  className="table-light"
                  style={{ position: "sticky", top: 0, zIndex: 10 }}
                >
                  <tr>
                    <th
                      style={{
                        width: "6%",
                        padding: "10px 8px",
                        textAlign: "center",
                      }}
                    >
                      S.No.
                    </th>
                    <th
                      style={{
                        width: "14%",
                        padding: "10px 8px",
                        textAlign: "left",
                      }}
                    >
                      Admission No.
                    </th>
                    <th
                      style={{
                        width: "18%",
                        padding: "10px 8px",
                        textAlign: "left",
                      }}
                    >
                      Student Name
                    </th>
                    <th
                      style={{
                        width: "14%",
                        padding: "10px 8px",
                        textAlign: "center",
                      }}
                    >
                      Status
                    </th>
                    <th
                      style={{
                        width: "7%",
                        padding: "10px 8px",
                        textAlign: "center",
                      }}
                    >
                      Rank
                    </th>
                    <th
                      style={{
                        width: "10%",
                        padding: "10px 8px",
                        textAlign: "center",
                      }}
                    >
                      Overall %
                    </th>
                    <th
                      style={{
                        width: "10%",
                        padding: "10px 8px",
                        textAlign: "center",
                      }}
                    >
                      Attendance %
                    </th>
                    <th
                      style={{
                        width: "7%",
                        padding: "10px 8px",
                        textAlign: "center",
                      }}
                    >
                      Version
                    </th>
                    <th
                      style={{
                        width: "14%",
                        padding: "10px 8px",
                        textAlign: "center",
                      }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {!classId || !sectionId || !selectedReportCycleId ? (
                    <tr>
                      <td
                        colSpan="9"
                        style={{ padding: "60px 20px", textAlign: "center" }}
                      >
                        <div style={{ fontSize: "48px", marginBottom: "12px" }}>
                          🔍
                        </div>
                        <h6 className="text-muted">
                          Select Class, Section & Report Cycle
                        </h6>
                        <p className="text-muted small">
                          Please select all required filters to view students
                        </p>
                      </td>
                    </tr>
                  ) : loading ? (
                    <tr>
                      <td
                        colSpan="9"
                        style={{ padding: "60px 20px", textAlign: "center" }}
                      >
                        <div
                          className="spinner-border text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2 text-muted">
                          Fetching students data...
                        </p>
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td
                        colSpan="9"
                        style={{ padding: "60px 20px", textAlign: "center" }}
                      >
                        <div style={{ fontSize: "48px", marginBottom: "12px" }}>
                          ❌
                        </div>
                        <h6 className="text-danger">Error Loading Data</h6>
                        <p className="text-muted small">{error}</p>
                      </td>
                    </tr>
                  ) : paginatedStudents.length === 0 ? (
                    <tr>
                      <td
                        colSpan="9"
                        style={{ padding: "60px 20px", textAlign: "center" }}
                      >
                        <div style={{ fontSize: "48px", marginBottom: "12px" }}>
                          📄
                        </div>
                        <h6 className="text-muted">No Students Found</h6>
                        <p className="text-muted small">
                          {searchQuery
                            ? "Try adjusting your search"
                            : "No students available for this class"}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    paginatedStudents.map((student, index) => {
                      const serialNumber = startIndex + index + 1;
                      return (
                        <tr key={student._id}>
                          <td
                            style={{
                              padding: "10px 8px",
                              textAlign: "center",
                              fontWeight: "500",
                            }}
                          >
                            {serialNumber}
                          </td>
                          <td
                            style={{ padding: "10px 8px", fontWeight: "500" }}
                          >
                            {student.admissionNo || "-"}
                          </td>
                          <td
                            style={{ padding: "10px 8px", fontWeight: "500" }}
                          >
                            {student.name || "-"}
                          </td>
                          <td
                            style={{ padding: "10px 8px", textAlign: "center" }}
                          >
                            <StatusBadge status={student.status} />
                          </td>
                          <td
                            style={{
                              padding: "10px 8px",
                              textAlign: "center",
                              fontWeight: "600",
                            }}
                          >
                            {student.rank || "-"}
                          </td>
                          <td
                            style={{ padding: "10px 8px", textAlign: "center" }}
                          >
                            {student.percentage || "-"}%
                          </td>
                          <td
                            style={{ padding: "10px 8px", textAlign: "center" }}
                          >
                            {student.attendancePercentage || "-"}%
                          </td>
                          <td
                            style={{ padding: "10px 8px", textAlign: "center" }}
                          >
                            <span
                              className="badge bg-secondary"
                              style={{ fontSize: "11px" }}
                            >
                              V{student.version || "-"}
                            </span>
                          </td>
                          <td
                            style={{ padding: "10px 8px", textAlign: "center" }}
                          >
                            <StatusActions
                              status={student.status}
                              student={student}
                              report={student.report}
                              reportCycles={reportCycles}
                              standardData={standardData}
                              schoolData={schoolData}
                              standardArr={standardArr}
                              sectionArr={sectionArr}
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination - Always visible at bottom */}
          {filteredStudents.length > 0 && (
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-3 py-2 border-top">
              <span className="text-muted small" style={{ fontSize: "12px" }}>
                Showing {startIndex + 1} to{" "}
                {Math.min(endIndex, filteredStudents.length)} of{" "}
                {filteredStudents.length} entries
              </span>
              <div className="d-flex align-items-center gap-1">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={goToPrevious}
                  disabled={currentPage === 1}
                  style={{ borderRadius: "6px" }}
                >
                  ‹
                </button>
                {getPageNumbers().map((page, idx) =>
                  page === "..." ? (
                    <span key={`ellipsis-${idx}`} className="px-2 text-muted">
                      …
                    </span>
                  ) : (
                    <button
                      key={page}
                      className={`btn btn-sm ${currentPage === page ? "btn-primary" : "btn-outline-secondary"}`}
                      onClick={() => goToPage(page)}
                      style={{ borderRadius: "6px", minWidth: "32px" }}
                    >
                      {page}
                    </button>
                  ),
                )}
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={goToNext}
                  disabled={currentPage === totalPages}
                  style={{ borderRadius: "6px" }}
                >
                  ›
                </button>
                <select
                  className="form-select form-select-sm ms-2"
                  style={{ width: "70px", borderRadius: "6px" }}
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value="8">8</option>
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Footer Buttons */}
        {isTopSelectionComplete && hasGlobalSessions && (
          <div className="row g-3 mt-2">
            {!hasReportsGenerated ? (
              <div className="col-12">
                <div className="card shadow-sm border-0 bg-primary bg-opacity-10">
                  <div className="card-body d-flex align-items-center gap-4">
                    <div style={{ fontSize: "40px" }}>📄</div>
                    <div className="flex-grow-1">
                      <h6 className="mb-1">Generate Reports</h6>
                      <p className="text-muted small mb-0">
                        Generate report cards for all students in this class
                        with the latest marks and data.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="col-md-6">
                  <div className="card shadow-sm border-0 bg-success bg-opacity-10 h-100">
                    <div className="card-body d-flex align-items-center gap-4">
                      <div style={{ fontSize: "40px" }}>📤</div>
                      <div>
                        <h6 className="mb-1">Publish Reports</h6>
                        <p className="text-muted small mb-0">
                          Publish all generated report cards. Once published,
                          reports will be visible to parents.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="card shadow-sm border-0 bg-warning bg-opacity-10 h-100">
                    <div className="card-body d-flex align-items-center gap-4">
                      <div style={{ fontSize: "40px" }}>🔄</div>
                      <div className="flex-grow-1">
                        <h6 className="mb-1">Re-Generate Reports</h6>
                        <p className="text-muted small mb-0">
                          Regenerate report cards with updated marks and data.
                        </p>
                      </div>
                      <button
                        className="btn btn-warning"
                        disabled={bulkLoading.regenerate}
                        style={{ borderRadius: "8px" }}
                      >
                        {bulkLoading.regenerate
                          ? "⏳ Regenerating..."
                          : "🔄 Re-Generate"}
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OfflinePerformanceReport;
